'use client';

import type { ReactNode } from 'react';
import {
  FaBolt,
  FaCrosshairs,
  FaDice,
  FaFire,
  FaTrophy,
  GiCrossedSwords,
  GiOpenChest,
  RiPercentLine,
} from '../../components/icons';
import { Credits } from '../../components/Credits';
import translations from './BandScreen.translations';

export type BandTranslationKey = keyof (typeof translations)['ru'];
export type TFn = (key: BandTranslationKey) => string;

export interface QuestCopy {
  /** Text before the coin amount — or the whole title, when the quest has no coin amount to show. */
  prefixKey: BandTranslationKey;
  /** Coin threshold embedded in the title, rendered via <Credits> — omitted for count-only quests. */
  creditsAmount?: number;
  /** Text after the coin amount. */
  suffixKey?: BandTranslationKey;
  /** Progress caption unit label ("кейса", "батла"...) — unused for accumulate-type quests. */
  unitKey: BandTranslationKey;
  /** Mirrors the backend pool's `accumulateAmount` (see band-quests.ts): progress is a running
   *  coin total toward a large target, not a small count of discrete actions — captioned with
   *  Credits/Credits instead of `current/target unit`. */
  accumulateAmount?: boolean;
  icon: ReactNode;
}

/** Copy and iconography per quest id. A quest with no entry here falls back to the generic
 *  title — the pool rotates in new ids ahead of their translations. */
export const QUEST_COPY: Record<string, QuestCopy> = {
  open_3_cases_2500: {
    prefixKey: 'quest_open_3_cases_2500_prefix',
    creditsAmount: 2_500,
    suffixKey: 'quest_open_3_cases_2500_suffix',
    unitKey: 'quest_open_3_cases_2500_unit',
    icon: <GiOpenChest size={18} />,
  },
  rush_3_matches: {
    prefixKey: 'quest_rush_3_matches_prefix',
    unitKey: 'quest_rush_3_matches_unit',
    icon: <GiCrossedSwords size={18} />,
  },
  open_band_case: {
    prefixKey: 'quest_open_band_case_prefix',
    unitKey: 'quest_open_band_case_unit',
    icon: <GiOpenChest size={18} />,
  },
  wager_25k: {
    prefixKey: 'quest_wager_25k_prefix',
    creditsAmount: 25_000,
    suffixKey: 'quest_wager_25k_suffix',
    unitKey: 'questGenericUnit',
    accumulateAmount: true,
    icon: <RiPercentLine size={18} />,
  },
  battles_2_5k: {
    prefixKey: 'quest_battles_2_5k_prefix',
    creditsAmount: 5_000,
    suffixKey: 'quest_battles_2_5k_suffix',
    unitKey: 'quest_battles_2_5k_unit',
    icon: <GiCrossedSwords size={18} />,
  },
  upgrades_3_2500: {
    prefixKey: 'quest_upgrades_3_2500_prefix',
    creditsAmount: 2_500,
    suffixKey: 'quest_upgrades_3_2500_suffix',
    unitKey: 'quest_upgrades_3_2500_unit',
    icon: <FaBolt size={16} />,
  },
  contract_10k: {
    prefixKey: 'quest_contract_10k_prefix',
    creditsAmount: 10_000,
    suffixKey: 'quest_contract_10k_suffix',
    unitKey: 'quest_contract_10k_unit',
    icon: <FaFire size={16} />,
  },
  win_battle: {
    prefixKey: 'quest_win_battle_prefix',
    unitKey: 'quest_win_battle_unit',
    icon: <FaTrophy size={16} />,
  },
  win_upgrade: {
    prefixKey: 'quest_win_upgrade_prefix',
    unitKey: 'quest_win_upgrade_unit',
    icon: <FaBolt size={16} />,
  },
  open_case_12500: {
    prefixKey: 'quest_open_case_12500_prefix',
    creditsAmount: 12_500,
    suffixKey: 'quest_open_case_12500_suffix',
    unitKey: 'quest_open_case_12500_unit',
    icon: <GiOpenChest size={18} />,
  },
  open_5_cases_1000: {
    prefixKey: 'quest_open_5_cases_1000_prefix',
    creditsAmount: 1_000,
    suffixKey: 'quest_open_5_cases_1000_suffix',
    unitKey: 'quest_open_5_cases_1000_unit',
    icon: <GiOpenChest size={18} />,
  },
  roulette_play: {
    prefixKey: 'quest_roulette_play_prefix',
    unitKey: 'quest_roulette_play_unit',
    icon: <FaDice size={16} />,
  },
  contracts_25k_sum: {
    prefixKey: 'quest_contracts_25k_sum_prefix',
    creditsAmount: 25_000,
    suffixKey: 'quest_contracts_25k_sum_suffix',
    unitKey: 'questGenericUnit',
    accumulateAmount: true,
    icon: <FaFire size={16} />,
  },
  upgrades_15k_sum: {
    prefixKey: 'quest_upgrades_15k_sum_prefix',
    creditsAmount: 15_000,
    suffixKey: 'quest_upgrades_15k_sum_suffix',
    unitKey: 'questGenericUnit',
    accumulateAmount: true,
    icon: <FaBolt size={16} />,
  },
  rush_5_matches: {
    prefixKey: 'quest_rush_5_matches_prefix',
    unitKey: 'quest_rush_5_matches_unit',
    icon: <FaCrosshairs size={16} />,
  },
  wager_50k: {
    prefixKey: 'quest_wager_50k_prefix',
    creditsAmount: 50_000,
    suffixKey: 'quest_wager_50k_suffix',
    unitKey: 'questGenericUnit',
    accumulateAmount: true,
    icon: <RiPercentLine size={18} />,
  },
  wager_10k_battles: {
    prefixKey: 'quest_wager_10k_battles_prefix',
    creditsAmount: 10_000,
    suffixKey: 'quest_wager_10k_battles_suffix',
    unitKey: 'questGenericUnit',
    accumulateAmount: true,
    icon: <GiCrossedSwords size={18} />,
  },
  wager_10k_rush: {
    prefixKey: 'quest_wager_10k_rush_prefix',
    creditsAmount: 10_000,
    suffixKey: 'quest_wager_10k_rush_suffix',
    unitKey: 'questGenericUnit',
    accumulateAmount: true,
    icon: <FaCrosshairs size={16} />,
  },
  wager_10k_roulette: {
    prefixKey: 'quest_wager_10k_roulette_prefix',
    creditsAmount: 10_000,
    suffixKey: 'quest_wager_10k_roulette_suffix',
    unitKey: 'questGenericUnit',
    accumulateAmount: true,
    icon: <FaDice size={16} />,
  },
  wager_100k: {
    prefixKey: 'quest_wager_100k_prefix',
    creditsAmount: 100_000,
    suffixKey: 'quest_wager_100k_suffix',
    unitKey: 'questGenericUnit',
    accumulateAmount: true,
    icon: <RiPercentLine size={18} />,
  },
  rush_mid_win: {
    prefixKey: 'quest_rush_mid_win_prefix',
    unitKey: 'quest_rush_mid_win_unit',
    icon: <FaTrophy size={16} />,
  },
  big_drop_25k: {
    prefixKey: 'quest_big_drop_25k_prefix',
    creditsAmount: 25_000,
    suffixKey: 'quest_big_drop_25k_suffix',
    unitKey: 'quest_big_drop_25k_unit',
    icon: <GiOpenChest size={18} />,
  },
  risky_upgrade_35: {
    prefixKey: 'quest_risky_upgrade_35_prefix',
    unitKey: 'quest_risky_upgrade_35_unit',
    icon: <FaBolt size={16} />,
  },
  covert_drop: {
    prefixKey: 'quest_covert_drop_prefix',
    unitKey: 'quest_covert_drop_unit',
    icon: <GiOpenChest size={18} />,
  },
};

export function renderQuestTitle(copy: QuestCopy | undefined, t: TFn): ReactNode {
  if (!copy) return t('questGenericTitle');
  if (copy.creditsAmount == null) return t(copy.prefixKey);
  return (
    <>
      {t(copy.prefixKey)} <Credits amount={copy.creditsAmount} iconSize={13} className="font-semibold" />{' '}
      {copy.suffixKey ? t(copy.suffixKey) : null}
    </>
  );
}
